import React, { useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { ChevronLeft, ChevronRight } from "react-feather";
import Calendar from "react-calendar";
import debounce from "lodash/debounce";
import {
  format,
  isSameDay,
  isWithinInterval,
  parseISO,
  startOfMonth,
  endOfDay,
  startOfDay,
  isEqual,
} from "date-fns";

import {
  setDiaryDate,
  fetchDiaryEvents,
} from "../../store/actions/events.actions";
import { useTypedSelector } from "../../store/models/root.interface";

const EventsCalendar: React.FC = () => {
  const dispatch = useDispatch();
  const eventsByUid = useTypedSelector((state) => state.event.eventsByUid);
  const diary = useTypedSelector((state) => state.event.diary);
  const fetchEvents = useCallback(
    debounce((date: Date) => {
      dispatch(fetchDiaryEvents(date));
    }, 200),
    []
  );

  const items = useMemo(() => {
    const key = format(diary.date, "yyyy-MM");
    const data = diary.eventUids[key];
    const items = data && typeof data !== "string"
      ? data.map(uid => eventsByUid[uid]).filter(x => !x?.isRound)
      : [];
    return items;
  }, [diary.date, diary.eventUids]);

  const now = new Date();

  useEffect(() => {
    dispatch(fetchDiaryEvents(diary.date));
  }, []);

  return (
    <div className="c-event-calendar">
      <Calendar
        activeStartDate={diary.date}
        formatShortWeekday={(locale: any, date: Date) => format(date, "EEEEE")}
        tileClassName={({ date, view }) => {
          let css: string[] = [];
          if (view === "month" && isSameDay(date, now)) {
            css.push(
              "react-calendar__tile react-calendar__month-view__days__day--today"
            );
          }
          if (
            items.some(x =>
              x &&
              !isEqual(date, parseISO(x.end)) &&
              isWithinInterval(date, {
                start: startOfDay(parseISO(x.start)),
                end: endOfDay(parseISO(x.end)),
              })
            )
          ) {
            css.push(
              "react-calendar__tile react-calendar__month-view__days__day--events"
            );
          }
          return css;
        }}
        prevLabel={<ChevronLeft width={16} height={16} />}
        nextLabel={<ChevronRight width={16} height={16} />}
        onActiveStartDateChange={(e) => {
          const date = startOfMonth(e.activeStartDate);
          dispatch(setDiaryDate(date));
          fetchEvents(date);
        }}
      />
    </div>
  );
};

export default EventsCalendar;
